import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { BUNDLES, MODULE_PRICES, BASE_PLATFORM_PRICE, PRODUCTS, rm } from "@/data/content";
import { Section } from "@/components/shared";
import PlanBuilder from "@/components/PlanBuilder";
import { ProductIcon } from "@/lib/icons";

const Pricing = () => {
  const { t, pick } = useLanguage();
  const tiers = t("pricing.tiers");

  const bundlePrice = (b) => {
    const sum = BASE_PLATFORM_PRICE + b.modules.reduce((a, id) => a + (MODULE_PRICES.find((m) => m.id === id)?.price || 0), 0);
    return Math.round(sum * (1 - b.saving / 100));
  };

  const ctaMap = { startFree: { to: "/signup", label: t("common.startFree") }, bookDemo: { to: "/contact", label: t("common.bookDemo") }, contactSales: { to: "/contact", label: t("common.contactSales") } };

  return (
    <div data-testid="pricing-page">
      <Section eyebrow={t("pricing.eyebrow")} title={t("pricing.title")} sub={t("pricing.sub")} className="pt-20 sm:pt-28 grid-bg">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {tiers.map((tier) => {
            const cta = ctaMap[tier.cta];
            const featured = tier.id === "business";
            return (
              <div
                key={tier.id}
                data-testid={`tier-${tier.id}`}
                className={`rounded-3xl p-7 flex flex-col border transition-all duration-300 ${
                  featured ? "bg-indigo-500/10 border-indigo-500/50 shadow-2xl shadow-indigo-500/20 lg:-mt-4 lg:mb-4" : "card-surface card-surface-hover"
                }`}
              >
                <h3 className="text-sm font-mono2 uppercase tracking-[0.2em] text-slate-300 mb-1">{tier.name}</h3>
                <p className="text-xs text-slate-500 mb-6">{tier.tag}</p>
                {tier.price ? (
                  <>
                    <p className="text-[11px] uppercase tracking-widest text-slate-500">{t("pricing.startingFrom")}</p>
                    <p className="text-3xl font-extrabold font-heading text-white mb-6">
                      {rm(tier.price)}<span className="text-sm font-medium text-slate-500">{t("common.perMonth")}</span>
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-extrabold font-heading text-white mb-6 mt-4">{t("pricing.customPricing")}</p>
                )}
                <ul className="space-y-2.5 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                      <Check className={`w-4 h-4 shrink-0 ${featured ? "text-indigo-400" : "text-emerald-400"}`} /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={cta.to}
                  data-testid={`tier-cta-${tier.id}`}
                  className={`inline-flex items-center justify-center gap-2 rounded-full font-semibold px-6 py-3 text-sm transition-colors ${
                    featured ? "bg-indigo-500 hover:bg-indigo-400 text-white shadow-lg shadow-indigo-500/30" : "border border-slate-700 hover:border-slate-500 text-slate-200"
                  }`}
                >
                  {cta.label} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </Section>

      <Section eyebrow={t("pricing.builderEyebrow")} title={t("pricing.builderTitle")} className="bg-[#05070B] pt-0">
        <PlanBuilder />
      </Section>

      <Section eyebrow={t("pricing.bundlesEyebrow")} title={t("pricing.bundlesTitle")}>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {BUNDLES.map((b) => (
            <div key={b.id} data-testid={`bundle-${b.id}`} className="card-surface card-surface-hover rounded-3xl p-6 flex flex-col">
              <h3 className="text-sm font-mono2 uppercase tracking-[0.2em] text-white mb-5">{b.name}</h3>
              <div className="flex flex-wrap gap-1.5 mb-6 flex-1">
                {b.modules.map((slug) => {
                  const p = PRODUCTS.find((x) => x.slug === slug);
                  return p ? (
                    <span key={slug} className={`inline-flex items-center gap-1.5 text-[10px] font-mono2 px-2 py-1 rounded-full ${p.accent.badge}`}>
                      <ProductIcon name={p.icon} className="w-3 h-3" /> {p.short}
                    </span>
                  ) : null;
                })}
              </div>
              {b.custom ? (
                <p className="text-sm font-semibold text-indigo-300 mb-5">{t("pricing.specialPricing")}</p>
              ) : (
                <>
                  <p className="text-2xl font-extrabold font-heading text-white">{rm(bundlePrice(b))}<span className="text-xs font-medium text-slate-500">{t("common.perMonth")}</span></p>
                  <p className="text-xs text-emerald-400 mb-5 mt-1">{t("pricing.bundleSaving")}: {b.saving}%</p>
                </>
              )}
              <Link to="/contact" data-testid={`bundle-cta-${b.id}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-700 hover:border-indigo-500/60 text-slate-200 font-semibold px-5 py-2.5 text-sm transition-colors">
                {t("common.contactSales")}
              </Link>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default Pricing;
